<template>
  <Card :class="['lead-activities']">
    <template #header>
      <div class="card-v2__title">{{ title }}</div>
      <div class="lead-activities__filter">
        <span style="margin-right: 3px">{{ $t('show') }}:</span>
        <ButtonComponent
          v-for="category in categories"
          v-bind="category.props"
          @click="() => onCategoryClick(category)"
        />
      </div>
    </template>
    <template #default>
      <LoaderComponent :value="promises.fetch" theme="default">
        <div class="lead-activities__list" ref="list">
          <Compact
            v-for="item in sortedItems"
            :key="item.id"
            :item="item"
            :scope="scope"
            @submit="() => submit(item)"
            @remove="() => remove(item)"
            class="lead-activities__list-item"
          />
          <div v-if="!sortedItems.length" class="text--center fade my-3">
            <i>{{ $t('noActivities') }}</i>
          </div>
        </div>

        <form @submit.prevent="() => submit()" class="lead-activities-input">
          <textarea
            v-model="activity.message"
            v-auto-height
            :placeholder="$t('typeHere') + '...'"
            @keypress.enter="() => submit()"
          ></textarea>
          <div data-part="date">
            <span>{{ $t('date') }}:</span>
            <input
              v-model="activityDate"
              data-part="date"
              type="datetime-local"
              :max="moment().format('YYYY-MM-DDTHH:mm')"
            />
          </div>

          <div v-if="$store.getters.isAdmin || quickNotes.length > 0" class="lead-activities__quick-actions">
            <ButtonComponent
              v-for="q in quickNotes"
              v-bind="q"
              :style="`--c-h: ${q.hsl.h}; --c-s: ${q.hsl.s}; --c-l: ${q.hsl.l};`"
              class="lead-activities__quick-actions__quick-note"
              @click="() => quickNote(q)"
            />

            <ButtonComponent
              v-if="$store.getters.isAdmin"
              :to="{ name: 'settings', params: { step: 'accountsettings' } }"
              :label="$t('createTemplate')"
              icon="fa-pro-light:plus"
              class="lead-activities__quick-actions__quick-note"
              tag="router-link"
            />
          </div>
          <div class="lead-activities-input__reminder">
            <CheckboxComponent v-model="activity.reminder.enabled" :label="$t('remindMe')" />
            <input
              v-model="activityReminderDate"
              type="date"
              data-part="reminder-date"
              :min="moment().add(1, 'day').startOf('day').format('YYYY-MM-DD')"
            />
          </div>
          <ButtonComponent :label="$t('add')" icon="fa-pro-light:plus" :promise="promises.mutate" type="submit" />
        </form>
      </LoaderComponent>
    </template>
  </Card>
</template>

<script>
import moment from 'moment'
import { mapState, mapActions, mapMutations } from 'vuex'
import { LoaderComponent } from 'vue-elder-loader'
import { DropdownComponent } from 'vue-elder-dropdown'
import { CheckboxComponent } from 'vue-elder-checkbox'
import ButtonGroup from '@kvass/button-group'
import Avatar from '@/components/Avatar'
import { SortByKey, HexToHsl } from '@/utils'
import { DatetimeTransformer, DateTransformer } from '@/utils/transformers'
import { InputComponent } from 'vue-elder-input'
import autoHeight from '@/directives/auto-height'
import Messages from './Message'
import TaskModel from '@/models/Task'

import EditableBoard from '../EditableBoard.vue'
import Card from '../Card-v2.vue'
import Model from '@/models/Activity/Create'
import Compact from './Themes/Compact.vue'

export default {
  props: {
    lead: {
      type: String,
      required: true,
    },
    scope: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default() {
        return this.$t('activityLog')
      },
    },
    autofocus: Boolean,
    anchoring: {
      type: String,
      default: 'bottom',
      validator: v => ['top', 'bottom'].includes(v),
    },
  },
  data() {
    return {
      filter: {
        categories: ['note', 'milestone', 'analytics'],
      },
      activity: this.getTemplate(),
    }
  },
  watch: {
    lead: {
      handler: 'fetch',
      immediate: true,
    },
    filter: {
      handler: 'fetch',
      deep: true,
    },
    sortedItems: {
      handler: 'scrollToAnchor',
      immediate: true,
    },
  },
  computed: {
    ...mapState('Activity', ['items', 'promises']),
    sortedItems() {
      const direction = this.anchoring === 'bottom' ? 'ASC' : 'DESC'
      return this.items.sort(SortByKey('date', { direction }))
    },
    quickNotes() {
      const notes = this.$store.state.enums.ActivitySettings.quickNotes || []
      return notes.map(note => {
        const hsl = HexToHsl(note.color)
        return {
          ...note,
          hsl: {
            h: `${hsl.h}deg`,
            s: `${hsl.s}%`,
            l: `${hsl.l}%`,
          },
        }
      })
    },
    columns() {
      return [
        { id: 'message', label: this.$tc('note', 2), size: '3fr' },
        { id: 'createdAt', label: this.$t('date'), size: '1fr' },
        { id: 'actions', label: '', size: '120px', align: 'end' },
      ]
    },
    activityDate: DatetimeTransformer('activity.date'),
    activityReminderDate: DateTransformer('activity.reminder.date'),
    categories() {
      return [
        { id: 'note', label: this.$tc('note', 2) },
        { id: 'milestone', label: this.$tc('milestone', 2) },
        { id: 'analytics', label: this.$t('userBehavior') },
      ].map(e => {
        const isActive = this.filter.categories.includes(e.id)
        return {
          ...e,
          props: {
            label: e.label,
            'data-active': isActive,
            'data-category': e.id,
          },
        }
      })
    },
  },
  methods: {
    ...mapActions('Activity', { doFetch: 'fetch', doCreate: 'mutate', doRemove: 'remove' }),
    ...mapMutations('Activity', ['set']),
    getIcon(item) {
      if (item.event === 'note') return 'fa-pro-light:note-sticky'
      if (item.event.startsWith('milestone:purchase:')) return 'fa-pro-light:party-horn'
      if (item.event.startsWith('milestone:unpurchase:')) return 'fa-pro-light:thumbs-down'
      if (item.event.startsWith('milestone:')) return 'fa-pro-light:flag-pennant'
      if (item.event.endsWith(':favorite:residential')) return 'fa-pro-light:heart'
      if (item.event.endsWith(':floorplan:residential')) return 'fa-pro-light:ruler-combined'

      switch (item.data.screen_size) {
        case 'Mobile':
          return 'fa-pro-light:mobile'
        default:
          return 'fa-pro-light:desktop'
      }
    },
    quickNote(note) {
      this.activity.message = note.message || note.label
      this.submitNew()
    },
    onCategoryClick(category) {
      if (this.filter.categories.includes(category.id))
        this.filter.categories = this.filter.categories.filter(c => c !== category.id)
      else this.filter.categories.push(category.id)
    },
    getMessageComponent(item) {
      return Messages[item.event.replace(/\:/g, '-')] || Messages.default
    },
    getTemplate() {
      const { avatar, name } = this.$store.state.user
      return {
        author: { avatar, name },
        message: '',
        date: moment().toJSON(),
        reminder: {
          enabled: false,
          date: moment().add(1, 'day').startOf('day').toJSON(),
        },
      }
    },
    fetch() {
      return this.doFetch({ filter: { ...this.filter, reference: this.lead } }).then(() => {
        if (this.autofocus) this.$el.querySelector('textarea').focus()
        this.scrollToAnchor()
      })
    },
    submit(item) {
      const isNew = !item || !item.id
      if (!item) item = structuredClone(this.activity)

      if (!item.message.trim()) return

      const base = {
        message: item.message,
        date: item.date,
      }

      const meta = {
        reference: {
          ref: this.lead,
          onModel: 'Lead',
        },
        scope: {
          ref: this.scope.id,
          onModel: this.scope.onModel,
        },
        event: 'note',
      }

      const data = isNew ? { ...base, ...meta } : { ...base, id: item.id }

      return this.doCreate([data, { strategy: 'none' }]).then(newItem => {
        if (isNew) {
          this.set([...this.items, newItem])
          this.activity = this.getTemplate()
          if (item.reminder.enabled) {
            const task = TaskModel({
              title: newItem.message,
              dueAt: item.reminder.date,
              members: [this.$store.state.user.id],
              reference: { onModel: 'Activity', ref: newItem.id },
            })
            this.$store
              .dispatch('Task/mutate', [task, { strategy: (stateItem, { id }) => ({ ...task, id }) }])
              .then(data => this.$bus.$emit('task:create', data))
          }
        } else this.set([...this.items.filter(({ id }) => id !== newItem.id), newItem])

        this.$emit('create', newItem)
      })
    },

    moment(date) {
      return moment(date)
    },
    remove(item) {
      return this.doRemove({ id: item.id }).then(item => {
        this.set(this.items.filter(({ id }) => id !== item.id))
      })
    },
    scrollToAnchor() {
      if (!this.$refs.list) return
      this.$nextTick(() => (this.$refs.list.scrollTop = this.anchoring === 'bottom' ? this.$refs.list.scrollHeight : 0))
    },
  },
  directives: {
    autoHeight,
  },
  components: {
    LoaderComponent,
    EditableBoard,
    DropdownComponent,
    CheckboxComponent,
    ButtonGroup,
    Avatar,
    InputComponent,
    Card,
    Compact,
  },
}
</script>

<style lang="scss">
.lead-activities {
  & > .card-v2__header {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
  }

  .elder-input {
    &__field {
      background-color: transparent;

      &:has([type='datetime-local']:read-only) {
        border: none;
        textarea,
        input {
          padding: 0;
        }
      }
    }
  }

  &__icon {
    $size: 40px;

    --lead-activities-icon-background: white;

    font-size: 0.9em;

    width: $size;
    height: $size;

    aspect-ratio: 1;

    background-color: var(--lead-activities-icon-background);
    border: 1px solid var(--lead-activities-icon-border);
    border-radius: var(--border-radius);

    display: flex;
    align-items: center;
    justify-content: center;

    margin-right: 0.75rem;

    &[data-event='note'] {
      --lead-activities-icon-background: var(--k-ui-color-warning-lightest);
      --lead-activities-icon-border: #{css-darken('k-ui-color-warning-lightest', 15%)};
    }

    &[data-event^='analytics:'] {
      --lead-activities-icon-background: var(--secondary);
      --lead-activities-icon-border: #{css-darken('secondary', 10%)};
    }

    &[data-event^='milestone:'] {
      --lead-activities-icon-background: var(--k-ui-color-success-lightest);
      --lead-activities-icon-border: #{css-darken('k-ui-color-success-lightest', 15%)};
    }

    &[data-event^='milestone:purchase:'] {
      --lead-activities-icon-background: var(--k-ui-color-success-lightest);
      --lead-activities-icon-border: #{css-darken('k-ui-color-success-lightest', 15%)};
    }

    &[data-event^='milestone:unpurchase:'] {
      --lead-activities-icon-background: var(--k-ui-color-danger-lightest);
      --lead-activities-icon-border: #{css-darken('k-ui-color-danger-lightestt', 15%)};
    }
  }

  *:has(> .lead-activities__list) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    justify-content: space-between;

    height: 100%;
  }

  .lead-activities-input {
    background-color: var(--k-ui-color-neutral-lightest);
    border: 1px solid var(--k-ui-color-neutral);
    border-radius: var(--border-radius);

    padding: 1rem;

    position: relative;

    display: grid;
    grid-template-columns: auto 1fr;
    gap: 1em;

    grid-template-areas:
      'message message'
      'quickactions quickactions'
      'date reminder';

    textarea {
      grid-area: message;

      border: none;
      padding: 0 8rem 0 0;

      resize: none;

      &:focus-within {
        outline: none;
      }
    }

    [type='datetime-local'] {
      &:focus-within {
        outline: none;
      }
    }

    [data-part='date'] {
      grid-area: date;

      font-size: 0.8rem;

      display: flex;
      align-items: center;
      gap: 0.5em;

      span {
        font-weight: bold;
      }
    }

    &__reminder {
      grid-area: reminder;

      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 0.5em;

      white-space: nowrap;
      font-size: 0.8rem;

      .elder-checkbox {
        align-items: center;
      }

      .elder-checkbox__label {
        font-weight: bold;
      }

      input:not(:checked) + .elder-checkbox__box {
        background-color: rgba(white, 0.5);
      }
    }

    .lead-activities__quickactions {
      grid-area: quickactions;
    }

    [type='submit'] {
      position: absolute;
      top: 0;
      right: 0;
      grid-area: submit;

      border-top-left-radius: 0;
      border-bottom-right-radius: 0;

      border-top: none;
      border-right: none;
    }
  }

  &__filter,
  &__quick-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;

    font-size: 0.8em;

    .elder-button {
      padding: 3px 8px;
      gap: 0.5em;
      border-radius: 999px;
      background-color: none;
      height: min-content;
      border: 1px solid var(--border-color);

      &__label,
      &__icon {
        padding: 0;
      }
    }

    .lead-activities__quick-actions__quick-note.elder-button {
      color: #000000 !important;
      background-color: hsla(
        var(--c-h, var(--k-ui-color-neutral-h)),
        var(--c-s, var(--k-ui-color-neutral-s)),
        var(--c-l, var(--k-ui-color-neutral-l)),
        10%
      ) !important;
      border: 1px solid
        hsla(
          var(--c-h, var(--k-ui-color-neutral-h)),
          var(--c-s, var(--k-ui-color-neutral-s)),
          var(--c-l, var(--k-ui-color-neutral-l)),
          50%
        ) !important;

      .elder-button__label {
        color: inherit !important;
      }
    }

    .elder-button:focus:before {
      opacity: 0 !important;
    }

    .elder-button--warning,
    [data-category='note'][data-active='true'] {
      $color: #fdecaf;
      border-color: color.adjust($color, $lightness: -25%);
      background: $color;
    }

    [data-category='milestone'][data-active='true'] {
      $color: #deefc2;
      border-color: color.adjust($color, $lightness: -25%);
      background: $color;
    }

    .elder-button--secondary,
    [data-category='analytics'][data-active='true'] {
      $color: #ebf3f9;
      border-color: color.adjust($color, $lightness: -25%);
      background: $color;
    }
  }

  &__filter {
    .elder-button:not([data-active='true']) {
      text-decoration: line-through;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    gap: 0.5rem;

    max-height: calc(100vh - 500px);
    overflow-y: scroll;
    scrollbar-base-color: transparent;

    &:has(.lead-activities__list-item--edit) {
      .lead-activities__list-item:not(.lead-activities__list-item--edit) {
        opacity: 0.2;
      }
    }

    &-item {
      padding: 0.75rem;
      border-radius: var(--border-radius);
      border: 1px solid var(--k-ui-color-neutral);
      background-color: var(--k-ui-color-neutral-lightest);

      display: grid;
      grid-template-areas:
        'icon message message action'
        'icon time author action';
      grid-template-columns: auto auto 1fr;
      align-items: center;

      gap: 0.25em;

      [data-part='action'] {
        font-size: 0.8em;

        grid-area: action;
      }

      [data-part='author'],
      [data-part='time'] {
        font-size: 0.8em;
        opacity: 0.8;
      }

      [data-part='author'] {
        grid-area: author;

        &:before {
          content: '- ';
        }
      }
      [data-part='avatar'] {
        grid-area: avatar;
        margin-left: 1em;
      }
      [data-part='time'] {
        grid-area: time;

        input {
          padding: 0;
        }
      }
      [data-part='message'] {
        grid-area: message;
      }

      .lead-activities__icon {
        grid-area: icon;
      }
    }
  }
}
</style>
